import type { components } from '@connected/api-client/api.generated';
import type { ColumnDef } from '@tanstack/react-table';

import { api } from '@connected/api-client';
import { me } from '@connected/api-client/data/auth/me';
import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { Button } from '@connected/ui/components/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@connected/ui/components/dropdown-menu';
import { Skeleton } from '@connected/ui/components/skeleton';
import { Page, Section } from '@connected/ui/features/layouts';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { EllipsisVertical, Plus } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { DataTable } from '@/components/data-table';
import { Loading } from '@/components/loading';
import { cn } from '@/lib/utils';

export type ProjectData = components['schemas']['ListElectricalProjectQueryResultProject'];

export const Route = createFileRoute('/(auth)/projects/')({
    component: ProjectsPage,
    validateSearch: z.object({
        organizationId: z.string().optional(),
    }),
    pendingComponent: () => <Loading action="Loading projects" />,
    beforeLoad: async ({ context }) => {
        const currentUser = await context.queryClient.ensureQueryData(me.queryOptions());

        if (!currentUser) {
            throw redirect({
                to: '/login',
            });
        }

        if (!currentUser.IsAdmin) {
            throw redirect({
                to: '/',
            });
        }
    },
});

function ProjectsPage() {
    const { t } = useTranslation('projects');

    const search = Route.useSearch();
    const navigate = Route.useNavigate();

    const { currentUser } = useAuthentication({ redirectToLogin: true });

    const copyProjectMutation = api.useMutation('post', '/api/v2/electrical/projects/copy', {
        onSuccess: ({ ElectricalProjectVersionId }) => {
            toast.success(t('messages.projectCopied'));
            navigate({
                to: '/projects/$projectVersionId/applications/request-concerning',
                params: {
                    projectVersionId: ElectricalProjectVersionId!,
                },
            });
        },
        onError: (error) => {
            toast.error(error.Message);
        },
    });

    const columns = useMemo<ColumnDef<ProjectData>[]>(() => ([
        {
            accessorKey: 'Object',
            header: t('table.columns.objectLocation'),
            cell: ({ row }) => {
                const projectObject = row?.original?.Object;

                const street = projectObject?.Street;
                const streetNumber = projectObject?.StreetNumber;
                const postalCode = projectObject?.PostalCode;
                const locality = projectObject?.Locality;

                if (!street && !streetNumber && !postalCode && !locality) {
                    return t('table.noObjectLocation');
                }

                return (
                    <div className="flex flex-col">
                        <div className="flex flex-row gap-1">
                            {street && <span>{street}</span>}
                            {streetNumber && <span>{streetNumber}</span>}
                        </div>
                        <div className="flex flex-row gap-1">
                            {postalCode && <span>{postalCode?.toUpperCase()}</span>}
                            {locality && <span>{locality}</span>}
                        </div>
                    </div>
                );
            },
        },
        {
            accessorKey: 'CustomerName',
            header: t('table.columns.client'),
            cell: ({ row }) => {
                const customer = row.original;

                return <span className="text-wrap">{customer.CustomerName || t('table.noClient')}</span>;
            },
        },
        {
            accessorKey: 'ReportKinds',
            header: t('table.columns.inspectionType'),
            cell: ({ row }) => {
                const project = row.original;

                return (
                    <div className="flex flex-col gap-1">
                        {project.Reports?.map(reportKind => (
                            <span key={reportKind?.Name}>{reportKind?.Name}</span>
                        ))}
                    </div>
                );
            },
        },
        {
            accessorKey: 'CreateDate',
            header: t('table.columns.createDate'),
            cell: ({ row }) => {
                const project = row.original;

                return new Date(project.CreateDate!).toLocaleDateString('nl-NL', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',

                });
            },
        },
        {
            accessorKey: 'Documents',
            header: '',
            cell: ({ row }) => {
                const project = row.original;

                return (
                    <div className="flex flex-row justify-center">
                        <Link
                            to="/projects/$projectVersionId/documents"
                            className="inline-flex items-center justify-center whitespace-nowrap rounded-md p-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                            search={{
                                organizationId: search.organizationId!,
                            }}
                            params={{
                                projectVersionId: project.CurrentElectricalProjectVersionId!,
                            }}
                        >
                            {t('table.columns.documents')}
                        </Link>
                    </div>
                );
            },
        },
        {
            accessorKey: 'openProject',
            header: '',
            cell: ({ row }) => {
                const project = row.original;

                return (
                    <div className="flex flex-row justify-center">
                        <Link
                            to="/projects/$projectVersionId/applications/request-concerning"
                            className="inline-flex items-center justify-center whitespace-nowrap rounded-md p-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                            params={{
                                projectVersionId: project.CurrentElectricalProjectVersionId!,
                            }}
                        >
                            {t('actions.openProject')}
                        </Link>
                    </div>
                );
            },
        },
        {
            accessorKey: 'menuItems',
            header: '',
            cell: ({ row }) => {
                return (
                    <DropdownMenu>
                        <DropdownMenuTrigger disabled={copyProjectMutation.isPending} asChild>
                            <Button variant="ghost" size="icon">
                                <EllipsisVertical />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="flex flex-col gap-1">
                            <DropdownMenuItem>
                                <Button
                                    disabled={copyProjectMutation.isPending}
                                    className="w-full"
                                    variant="ghost"
                                    onClick={async () => {
                                        toast.info(t('messages.projectCopying'));
                                        await copyProjectMutation.mutate({
                                            body: {
                                                Identifier: row.original.Identifier!,
                                                ElectricalProjectId: row.original.ProjectId,
                                            },
                                        });
                                    }}
                                >
                                    {t('actions.copyProject')}
                                </Button>
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                                <Button disabled className="w-full" variant="ghost">
                                    {t('actions.archiveProject')}
                                </Button>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            },
        },

    ]), [copyProjectMutation, search.organizationId, t]);

    const projectMutation = api.useMutation(
        'post',
        '/api/v2/electrical/projects/create',
        {
            onSuccess: ({ ElectricalProjectVersionId }) => {
                toast.success(t('messages.projectCreated'));
                navigate({
                    to: '/projects/$projectVersionId/applications/request-concerning',
                    params: {
                        projectVersionId: ElectricalProjectVersionId!,
                    },
                });
            },
        },
    );

    const projectsQuery = useQuery(
        elektraAppQueryOptions.organisation.projects.projectListOptions({
            organizationId: currentUser?.Organization?.OrganizationId ?? search.organizationId,
            enabled: !!currentUser?.Organization?.OrganizationId || !!search.organizationId,
        }),

    );

    const tableData = useMemo<ProjectData[]>(
        () => {
            if (projectsQuery.isError) {
                toast.error(projectsQuery.error?.Message);
                return [];
            }

            if (!projectsQuery.data) {
                return [];
            }

            return projectsQuery.data.Projects;
        },
        [projectsQuery.data, projectsQuery.error?.Message, projectsQuery.isError],
    );

    const tableColumns = useMemo(
        () =>
            projectsQuery.isLoading
                ? columns.map(column => ({
                        ...column,
                        cell: () => (
                            <Skeleton className="h-8 w-full" />
                        ),
                    }))
                : columns,
        [columns, projectsQuery.isLoading],
    );

    function handleClick() {
        projectMutation.mutate({
            body: {
                OrganizationId: currentUser.Organization!.OrganizationId || search.organizationId || '',
            },
        });
    }

    if (!search.organizationId && !currentUser?.Organization?.OrganizationId && !currentUser?.IsAdmin) {
        toast.error(t('messages.noOrganisationFound'));
        return null;
    }

    if (!search.organizationId && !currentUser?.Organization?.OrganizationId && currentUser?.IsAdmin) {
        navigate({
            to: '/admin/organisations',
            search: {
                organizationId: search.organizationId,
            },
        });
        return null;
    }

    return (
        <Page className="xl:max-w-7xl">
            <Section>
                <Section.Header>
                    <Section.Title>{t('layout.title')}</Section.Title>
                    <Section.Header.Actions>
                        <Section.Header.Action
                            variant="default"
                            icon={<Plus />}
                            onClick={handleClick}
                            disabled={projectMutation.isPending}
                        />
                    </Section.Header.Actions>
                </Section.Header>
                <Section.Content>
                    <DataTable
                        columns={tableColumns}
                        data={tableData}
                        pageSize={window.innerHeight / 120} // hack to get a good page size
                        className="w-full bg-background-primary"
                        cellClassName={cell => cn('', {
                            'w-min-content': ['menuItems'].includes(cell.column.id),
                            'max-w-10': ['CustomerName'].includes(cell.column.id),
                            'max-w-28': ['Documents', 'openProject', 'CreateDate'].includes(cell.column.id),
                        })}
                    />
                </Section.Content>
            </Section>
        </Page>
    );
}
